import { Button } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  CalendarClock,
  Hourglass,
  RefreshCw,
  Search,
  ShieldCheck,
  Wrench,
} from 'lucide-react'

import { isChatWidgetEnabled } from '@/lib/feature-flags'

interface FlightErrorCardProps {
  onRetry?: () => void
  type?: 'error' | 'maintenance' | 'no-results' | 'rate-limited-day' | 'rate-limited-minute'
}

export function FlightErrorCard({ onRetry, type = 'no-results' }: FlightErrorCardProps) {
  const isError = type === 'error'
  const isMaintenance = type === 'maintenance'
  const isRateLimitedMinute = type === 'rate-limited-minute'
  const isRateLimitedDay = type === 'rate-limited-day'
  const isRateLimited = isRateLimitedMinute || isRateLimitedDay
  const isProblem = isError || isMaintenance || isRateLimited
  // Per-day rate-limit: retrying is useless until tomorrow's reset, hide it.
  const showRetry = !!onRetry && !isRateLimitedDay

  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white px-8 py-12 shadow-sm">
      <div className={`mb-6 flex h-20 w-20 items-center justify-center rounded-full ${isProblem ? 'bg-red-50' : 'bg-amber-50'}`}>
        {isMaintenance ? (
          <Wrench className="h-10 w-10 text-red-500" />
        ) : isRateLimitedDay ? (
          <CalendarClock className="h-10 w-10 text-red-500" />
        ) : isRateLimitedMinute ? (
          <Hourglass className="h-10 w-10 text-red-500" />
        ) : isError ? (
          <AlertTriangle className="h-10 w-10 text-red-500" />
        ) : (
          <Search className="h-10 w-10 text-amber-500" />
        )}
      </div>

      <h2 className="mb-2 text-xl font-semibold text-gray-900">
        {isMaintenance
          ? 'Maintenance en cours'
          : isRateLimitedDay
            ? 'Limite quotidienne atteinte'
            : isRateLimitedMinute
              ? 'Petite pause nécessaire'
              : isError
                ? 'Erreur de recherche'
                : 'Aucun résultat trouvé'}
      </h2>

      <p className="mb-6 max-w-md text-center text-gray-600">
        {isMaintenance
          ? "Notre partenaire de réservation de vols est temporairement indisponible. La recherche de vols devrait revenir très prochainement."
          : isRateLimitedDay
            ? "Vous avez atteint le nombre maximum de recherches autorisées pour aujourd'hui. Le compteur sera réinitialisé à minuit (heure de Paris)."
            : isRateLimitedMinute
              ? "Pour des raisons de sécurité, nous limitons le nombre de recherches par minute. Merci de patienter un court instant avant de relancer votre recherche."
              : isError
                ? "Une erreur est survenue lors de la recherche de vols. Cela peut être dû à un problème temporaire."
                : "Nous n'avons pas pu trouver de vols correspondant à votre recherche. Essayez avec des dates différentes ou une destination alternative."}
      </p>

      <div className={`mb-8 rounded-lg p-4 ${isProblem ? 'bg-red-50' : 'bg-sky-50'}`}>
        <p className={`mb-3 text-sm font-medium ${isProblem ? 'text-red-700' : 'text-sky-700'}`}>
          {isProblem ? 'Que faire ?' : 'Quelques conseils :'}
        </p>
        <ul className={`space-y-2 text-sm ${isProblem ? 'text-red-600' : 'text-sky-600'}`}>
          {isMaintenance ? (
            <>
              <li className="flex items-start gap-2">
                <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Réessayez dans quelques minutes</span>
              </li>
              <li className="flex items-start gap-2">
                <Wrench className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Le service est en cours de rétablissement, aucune action n&apos;est requise de votre part</span>
              </li>
            </>
          ) : isRateLimitedDay ? (
            <>
              <li className="flex items-start gap-2">
                <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Revenez demain : le quota est remis à zéro à minuit (heure de Paris)</span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Cette limite quotidienne protège le service contre les abus, elle s&apos;applique à tous les visiteurs</span>
              </li>
            </>
          ) : isRateLimitedMinute ? (
            <>
              <li className="flex items-start gap-2">
                <Hourglass className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Patientez environ une minute avant de relancer la recherche</span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Cette limite protège le service contre les abus, elle s&apos;applique à tous les visiteurs</span>
              </li>
            </>
          ) : isError ? (
            <>
              <li className="flex items-start gap-2">
                <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Réessayez dans quelques instants</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Si le problème persiste, contactez le support</span>
              </li>
            </>
          ) : (
            <>
              <li className="flex items-start gap-2">
                <span>📅</span>
                <span>Essayez des dates plus flexibles (quelques jours d&apos;écart)</span>
              </li>
              <li className="flex items-start gap-2">
                <span>✈️</span>
                <span>Vérifiez que les villes de départ et d&apos;arrivée sont correctes</span>
              </li>
              {isChatWidgetEnabled && (
                <li className="flex items-start gap-2">
                  <span>💬</span>
                  <span>Utilisez le chatbot pour affiner votre recherche avec notre aide</span>
                </li>
              )}
            </>
          )}
        </ul>
      </div>

      <div className="flex gap-3">
        {showRetry && (
          <Button
            className={isProblem ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-sky-200 text-sky-600 hover:bg-sky-50'}
            onClick={onRetry}
            variant="outline"
          >
            <RefreshCw className="size-4" />
            Réessayer
          </Button>
        )}
        <Link to="/">
          <Button className="bg-brand-600 hover:bg-brand-700">
            Nouvelle recherche
          </Button>
        </Link>
      </div>
    </div>
  )
}
